import React, {useCallback, useEffect, useState} from "react";
import {useStorageState} from "react-storage-hooks";
import BetriebsArbeitsListe from "../components/arbeitslisten/BetriebsArbeitsListe";
import MessstelleArbeitsListe from "../components/arbeitslisten/messstelle/MessstelleArbeitsListe";
import NachrichtenArbeitsliste from "../components/arbeitslisten/NachrichtenArbeitsliste";
import EATabs from "../components/EATabs";
import {handleApiException, useHttp} from "../services/HttpService";
import {countNachrichtenEndpoint} from "../services/NachrichtenService";
import WiedervorlageArbeitsliste from "../components/arbeitslisten/WiedervorlageArbeitsliste";
import {getWiedervorlagenEndpoint} from "../services/WiedervorlagenService";
import PersonalisierungsComponent from "../components/arbeitslisten/PersonalisierungsComponent";
import "../styles/views/startseite.css";
import {userHasAnyRole} from "../utils/SecurityUtils";
import {
    BEAUFTRAGTER,
    BETREIBER,
    FACHADMIN,
    SYSTEMADMIN,
    ZUST_SBA,
    ZUST_SBB,
    ZUST_SBD,
    ZUST_SBS,
} from "../consts/funktionaleRollen";
import {useForm} from "../utils/FormUtils";
import {getBehoerdeEndpoint, getRaeumlicheEndpoint, getRegionalstelleEndpoint} from "../services/WertelistenService";
import AnlageArbeitsListe from "../components/arbeitslisten/anlage/AnlageArbeitsListe";
import routes from "../consts/routes.json";
import JahresberichtArbeitsListe from "../components/arbeitslisten/jahresbericht/JahresberichtArbeitsListe";

const betriebOption = "Betriebe";
const anlageOption = "Anlagen";
const messstelleOption = "Messstellen";
const jahresberichtOption = "Jahresberichte";
const nachrichtenOption = "nachrichten";
const wiedervorlageOption = "Wiedervorlagen";
const tabOptions = [anlageOption, messstelleOption, betriebOption, jahresberichtOption, nachrichtenOption, wiedervorlageOption];
const tabOptionsBetreiber = [anlageOption, messstelleOption, jahresberichtOption, nachrichtenOption, wiedervorlageOption];
const tabOptionsLeser = [betriebOption, anlageOption, messstelleOption];

function Startseite({history}) {
    const [currentTabInSession, setCurrentTabInSession] = useStorageState(sessionStorage, "eab-home-current-tab", anlageOption);
    const [currentTab, setCurrentTab] = useState(currentTabInSession);
    const [sharedPersistentFilter, setSharedPersistentFilter] = useStorageState(sessionStorage, "eab-shared-filter", {});
    const {values: sharedFilter, setValues: setSharedFilter, formFactory: sharedFormFactory} = useForm({
        initialValues: sharedPersistentFilter,
    });
    const [{data: nachrichtenAnzahl, loading: nachrichtenLoading}, refetchNachrichten] = useHttp(countNachrichtenEndpoint, {
        manual: !userHasAnyRole([SYSTEMADMIN, FACHADMIN, BETREIBER, BEAUFTRAGTER, ZUST_SBD, ZUST_SBB, ZUST_SBS, ZUST_SBA]),
    });
    const [{data: wiedervorlagen, loading: wiedervorlagenLoading}, refetchWiedervorlagen] = useHttp(getWiedervorlagenEndpoint, {
        manual: !userHasAnyRole([SYSTEMADMIN, FACHADMIN, BETREIBER, BEAUFTRAGTER, ZUST_SBD, ZUST_SBB, ZUST_SBS, ZUST_SBA]),
    });

    const [regionalstelleOptionen] = useHttp(getRegionalstelleEndpoint);
    const [raeumlichesKriteriumOptionen] = useHttp(getRaeumlicheEndpoint);
    const [behoerdeOptionen] = useHttp(getBehoerdeEndpoint);

    function handleTabChange(value) {
        if (value !== nachrichtenOption && value !== wiedervorlageOption) {
            setCurrentTabInSession(value);
        }
        setCurrentTab(value);
        if (value === nachrichtenOption &&
            userHasAnyRole([SYSTEMADMIN, FACHADMIN, BETREIBER, BEAUFTRAGTER, ZUST_SBD, ZUST_SBB, ZUST_SBS, ZUST_SBA])) {
            refetchNachrichten().catch((e) => handleApiException(e, "Fehler beim Laden"));
        }
    }

    useEffect(() => {
        if (window.self !== window.top) {
            history.push(routes.LOGGED_IN_EMBEDDED);
        }
    }, [history]);

    /**
     * Speichert den gesetzten Filter im sessionStorage
     */
    useEffect(() => {
        if (sharedFilter) {
            setSharedPersistentFilter(sharedFilter);
        }
    }, [sharedFilter, setSharedPersistentFilter]);

    const handleErweiterteSucheOpenChanged = useCallback(
        (extendedSearchOpen) => {
            setSharedFilter((oldValues) => {
                return {...oldValues, extendedSearchOpen};
            });
        },
        [setSharedFilter]
    );

    const resetSharedFilter = useCallback(() => {
        setSharedFilter((oldValues) => (oldValues.extendedSearchOpen ? {extendedSearchOpen: true} : {}));
    }, [setSharedFilter]);

    return (
        <div className={"startseiteLayout gridl"}>
            <EATabs
                value={currentTab}
                options={
                    userHasAnyRole([BETREIBER])
                        ? tabOptionsBetreiber
                        : userHasAnyRole([SYSTEMADMIN, FACHADMIN, BEAUFTRAGTER, ZUST_SBD, ZUST_SBB, ZUST_SBS, ZUST_SBA])
                            ? tabOptions
                            : tabOptionsLeser
                }
                optionLabels={{
                    [nachrichtenOption]: `Nachrichten ${nachrichtenLoading ? "" : "(" + nachrichtenAnzahl + ")"}`,
                    [wiedervorlageOption]: `Wiedervorlagen ${wiedervorlagenLoading ? "" : "(" + wiedervorlagen?.length + ")"}`,
                }}
                onChange={handleTabChange}
            />
            <PersonalisierungsComponent
                setPersonalized={(newValue) =>
                    setSharedFilter((oldValues) => {
                        return {...oldValues, personalisiert: newValue};
                    })
                }
            />
            <div className={"startseiteContent"}>
                {currentTab === anlageOption && (
                    <AnlageArbeitsListe
                        history={history}
                        sharedFilter={sharedFilter}
                        resetSharedFilter={resetSharedFilter}
                        onErweiterteSucheOpenChanged={handleErweiterteSucheOpenChanged}
                        sharedFormFactory={sharedFormFactory}
                        regionalstelleOptionen={regionalstelleOptionen}
                        raeumlichesKriteriumOptionen={raeumlichesKriteriumOptionen}
                        behoerdeOptionen={behoerdeOptionen}
                    />
                )}
                {currentTab === messstelleOption && (
                    <MessstelleArbeitsListe
                        history={history}
                        sharedFilter={sharedFilter}
                        resetSharedFilter={resetSharedFilter}
                        onErweiterteSucheOpenChanged={handleErweiterteSucheOpenChanged}
                        sharedFormFactory={sharedFormFactory}
                        regionalstelleOptionen={regionalstelleOptionen}
                        raeumlichesKriteriumOptionen={raeumlichesKriteriumOptionen}
                        behoerdeOptionen={behoerdeOptionen}
                    />
                )}
                {currentTab === betriebOption && (
                    <BetriebsArbeitsListe
                        history={history}
                        sharedFilter={sharedFilter}
                        resetSharedFilter={resetSharedFilter}
                        onErweiterteSucheOpenChanged={handleErweiterteSucheOpenChanged}
                        sharedFormFactory={sharedFormFactory}
                        regionalstelleOptionen={regionalstelleOptionen}
                        raeumlichesKriteriumOptionen={raeumlichesKriteriumOptionen}
                        behoerdeOptionen={behoerdeOptionen}
                    />
                )}
                {currentTab === jahresberichtOption && (
                    <JahresberichtArbeitsListe
                        history={history}
                        sharedFilter={sharedFilter}
                        resetSharedFilter={resetSharedFilter}
                        onErweiterteSucheOpenChanged={handleErweiterteSucheOpenChanged}
                        sharedFormFactory={sharedFormFactory}
                        regionalstelleOptionen={regionalstelleOptionen}
                        raeumlichesKriteriumOptionen={raeumlichesKriteriumOptionen}
                        behoerdeOptionen={behoerdeOptionen}
                    />
                )}
                {currentTab === nachrichtenOption &&
                    userHasAnyRole([SYSTEMADMIN, FACHADMIN, BETREIBER, BEAUFTRAGTER, ZUST_SBD, ZUST_SBB, ZUST_SBS, ZUST_SBA]) && (
                        <NachrichtenArbeitsliste history={history} refetchNachrichten={refetchNachrichten}/>
                    )}
                {currentTab === wiedervorlageOption &&
                    userHasAnyRole([SYSTEMADMIN, FACHADMIN, BETREIBER, BEAUFTRAGTER, ZUST_SBD, ZUST_SBB, ZUST_SBS, ZUST_SBA]) && (
                        <WiedervorlageArbeitsliste data={wiedervorlagen} loading={wiedervorlagenLoading} refetch={refetchWiedervorlagen}/>
                    )}
            </div>
        </div>
    );
}

export default Startseite;
